import { Dialog, DialogBackdrop, DialogPanel, Transition } from "@headlessui/react";

import CloseIcon from "@assets/icons/close.svg";
import SearchIcon from "@assets/icons/search.svg";
import InfoCircleIcon from "@assets/icons/info.svg";

import { useSwitches } from "@hooks/useSwitches";
import { useEscapeKey } from "@hooks/useEscapeKey";
import { useCombobox } from "./hooks/useCombobox";
import { useMentions } from "./hooks/useMentions";
import { useScrollLock } from "@hooks/useScrollLock";
import { useVisualViewportAdjustment } from "@hooks/useVisualViewportAdjustment";
import { ITagResponse } from "@interfaces/responses.interface";
import { RelatedItem } from "@app-types/related-items.type";
import { TagsTypes } from "@components/Sections/TagsSection/constants";
import { matchRelatedItem } from "@utils/related-items.util";
import { cn } from "@utils/tailwind.util";
import {
  ICompanyTag,
  IDealTag,
  IDocumentTag,
  IPeopleTag,
  IThreadTag,
} from "@interfaces/tag.interface";
import { SuggestionCompany } from "@components/TaggingComponent/SuggestionItems/SuggestionCompany";
import { SuggestionDocument } from "@components/TaggingComponent/SuggestionItems/SuggestionDocument";
import { SuggestionMeeting } from "@components/TaggingComponent/SuggestionItems/SuggestionMeeting";
import { SuggestionPeople } from "@components/TaggingComponent/SuggestionItems/SuggestionPeople";
import { SuggestionThread } from "@components/TaggingComponent/SuggestionItems/SuggestionThread";
import { SuggestionDeal } from "@components/TaggingComponent/SuggestionItems/SuggestionDeal";
import { TaggingComponent } from "@components/TaggingComponent/TaggingComponent";

export type ISpotlightProps = {
  placeholderText?: string;
  query: string;
  visible: boolean;
  selectedTags: ITagResponse[];
  keyForTypes: keyof typeof TagsTypes;
  wrapperClassName?: string;
  containerClassName?: string;
  onChange: (query: string) => void;
  onClose: (mention?: RelatedItem) => void;
};

export function SpotlightCombobox({
  className,
  iconSize = 20,
  inputClassName,
  items,
  placeholderText,
  query,
  onChange,
  onSelect,
  uppercaseLabels = false,
  noResultsHeight,
  heightClassName,
  isCompact = false,
  showPeopleAndCompanies = false,
}: {
  className?: string;
  iconSize?: number;
  inputClassName?: string;
  items: RelatedItem[];
  placeholderText: string;
  query: string;
  onChange: (value: string) => void;
  onSelect: (mention?: RelatedItem) => void;
  uppercaseLabels?: boolean;
  heightClassName?: string;
  noResultsHeight?: string;
  isCompact?: boolean;
  showPeopleAndCompanies?: boolean;
}) {
  const finalHeightClassName = heightClassName ?? (isCompact ? "max-h-[240px]" : "");
  const finalNoResultsHeight = noResultsHeight ?? (isCompact ? "lg:h-[200px]" : "lg:h-72");
  const { groups } = useMentions(items);

  const { isAvailable } = useSwitches();

  const enableCompaniesAndPeople = isAvailable("COMPANIES_AND_PEOPLE");

  const { inputRef, optionsRef } = useCombobox({ items, onSelect });

  let posinset = 0;

  const filteredGroups = groups
    .filter(
      ([label]) =>
        ((showPeopleAndCompanies || (label !== "People" && label !== "Companies")) &&
          enableCompaniesAndPeople) ||
        (label !== "People" && label !== "Companies")
    )
    .sort((a, b) => {
      const order = ["Deals", "Companies", "People", "Meetings", "Documents", "Notes"];

      const indexA = order.indexOf(a[0]);
      const indexB = order.indexOf(b[0]);

      return indexA - indexB;
    });

  return (
    <div
      className={cn(
        "flex min-h-0 w-full flex-1 flex-col divide-y divide-stroke-primary dark:divide-gray-600",
        className
      )}
    >
      <div className="relative flex items-center px-4">
        <SearchIcon
          height={iconSize}
          width={iconSize}
          className="relative text-content-secondary"
        />

        <input
          ref={inputRef}
          value={query}
          autoComplete="off"
          aria-owns="options"
          aria-controls="options"
          aria-autocomplete="list"
          aria-expanded="true"
          autoCapitalize="off"
          role="combobox"
          className={cn(
            "w-full appearance-none border-0 p-4 text-sm outline-none placeholder:text-content-disabled focus:ring-0",
            inputClassName
          )}
          onChange={(ev) => {
            onChange(ev.target.value);
          }}
          placeholder={placeholderText}
        />

        {query && (
          <button
            type="button"
            className="absolute right-4 top-1/2 -translate-y-1/2 text-content-secondary hover:text-content-primary"
            onClick={() => onChange("")}
            aria-label="Clear search"
          >
            <CloseIcon height={20} width={20} />
          </button>
        )}
      </div>

      <div
        ref={optionsRef}
        role="listbox"
        id="options"
        tabIndex={-1}
        className={cn(finalHeightClassName, "flex-1 overflow-y-auto")}
      >
        {filteredGroups.length > 0 ? (
          filteredGroups.map(([label, options]) => {
            return (
              <div className="border-b px-3 pb-1 pt-3" key={label}>
                <div
                  className={cn("pb-1.5 text-xs font-medium text-content-secondary", {
                    uppercase: uppercaseLabels,
                  })}
                >
                  {label}
                </div>

                <ul className="space-y-1">
                  {options.map((item) => {
                    const position = posinset++;

                    return (
                      <li
                        key={"id" in item ? item.id : ""}
                        role="option"
                        id={`option-${position}`}
                        aria-posinset={position}
                        aria-setsize={items.length}
                        data-value={"id" in item ? item.id : ""}
                        aria-selected="false"
                        className="-mx-2 rounded-md hover:bg-background-secondary aria-selected:bg-background-secondary"
                        tabIndex={-1}
                        onClick={() => onSelect(item)}
                      >
                        {matchRelatedItem(item, {
                          onDeal: (deal) => <SuggestionDeal item={deal as unknown as IDealTag} />,
                          onCompanies: (company) => (
                            <SuggestionCompany item={company as unknown as ICompanyTag} />
                          ),
                          onPeople: (person) => (
                            <SuggestionPeople item={person as unknown as IPeopleTag} />
                          ),
                          onMeeting: (meeting) => <SuggestionMeeting item={meeting} />,
                          onDocument: (document) => (
                            <SuggestionDocument
                              id={document.id}
                              item={document as unknown as IDocumentTag}
                              onClick={() => null}
                            />
                          ),
                          onThread: (thread) => (
                            <SuggestionThread item={thread as unknown as IThreadTag} />
                          ),
                        })}
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })
        ) : query.length > 0 ? (
          <div
            className={cn(
              "flex h-full w-full flex-col items-center justify-center gap-3 text-sm font-medium text-content-secondary",
              finalNoResultsHeight
            )}
          >
            <SearchIcon width={20} height={20} />
            <p>No results</p>
          </div>
        ) : (
          <div
            className={cn(
              "flex h-full w-full flex-col items-center justify-center gap-3 p-4 text-sm font-medium text-content-secondary",
              finalNoResultsHeight
            )}
          >
            <InfoCircleIcon width={20} height={20} />
            <p>There are no deals, meetings, companies, person or documents yet</p>
          </div>
        )}
      </div>
    </div>
  );
}

export const BaseSpotlight = ({
  placeholderText = "Reference any object",
  query,
  visible = true,
  selectedTags,
  keyForTypes,
  wrapperClassName,
  containerClassName,
  onClose,
  onChange,
}: ISpotlightProps) => {
  const { offsetY, viewportHeight } = useVisualViewportAdjustment();

  const handleDialogClose = () => onClose();

  const handleBackdropClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    e.stopPropagation();
    onClose();
  };

  useEscapeKey({
    callback: () => onClose(),
    enabled: visible,
  });

  useScrollLock({ enabled: offsetY !== 0 });

  const excludeIds = selectedTags.map((value) => value.object.id) ?? [];

  return (
    <Transition
      appear
      show={visible}
      enter="transition duration-300 ease"
      enterFrom="transform  opacity-0"
      enterTo="transform  opacity-100"
      leave="transition duration-300 ease"
      leaveFrom="transform opacity-100"
      leaveTo="transform opacity-0"
    >
      <Dialog
        as="div"
        className={cn(
          "fixed inset-0 z-50 flex flex-col items-center justify-center",
          wrapperClassName
        )}
        onClose={handleDialogClose}
        style={{ height: viewportHeight }}
      >
        {/* backdrop */}
        <DialogBackdrop
          className="fixed inset-0 bg-black-500/20 backdrop-blur-[1px]"
          onClick={handleBackdropClick}
        />

        {/* content */}
        <DialogPanel
          className={cn(
            "relative mx-4 max-h-[clamp(320px,90%,640px)] divide-y divide-stroke-primary dark:divide-gray-600 rounded-md border border-stroke-primary dark:border-gray-600 bg-white dark:bg-gray-850 shadow-navItem",
            "flex h-full w-[calc(100%-16px)] max-w-3xl flex-col items-center justify-center",
            containerClassName
          )}
        >
          <TaggingComponent
            query={query}
            placeholder={placeholderText}
            onChange={onChange}
            onSelect={onClose}
            excludedIds={excludeIds}
            keyForTypes={keyForTypes}
            heightClassName="max-h-full"
            uppercaseLabels
            showShortcuts
          />
        </DialogPanel>
      </Dialog>
    </Transition>
  );
};
